import { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../context/auth-context";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { DataGrid, GridToolbar, GridActionsCellItem } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from '@mui/icons-material/Edit';
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

export const Shops = ({ onClose }) => {
  const [shops, setShops] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentShop, setCurrentShop] = useState({
    id: "",
    name: "",
    description: ""
  });
  const [editShop, setEditShop] = useState(false);

  const auth = useContext(AuthContext);

  const requestConfig = useMemo(
    () => ({
      headers: {
        Authorization: "Bearer " + auth.token,
      },
    }),
    [auth.token]
  );

  useEffect(() => {
    const getShops = async () => {
      try {
        const result = await axios.get("/api/shops/getshops", requestConfig);
        console.log(result.data.shops);
        setShops(result.data.shops);
        return;
      } catch (err) {
        alert("There was an error getting the shops");
        console.log("Error getting the shops :", err);
      }
    };
    getShops();
  }, [requestConfig]);

  const handleOpen = () => {
    setCurrentShop({
      id: "",
      name: "",
      description: ""
    });
    setEditShop(false);
    setOpen(true);
  };

  const handleAddShop = async (shop) => {
    const id = uuidv4();
    const newShop = { ...shop, id: id };
    console.log("Here is the added shop: ", newShop);

    const newShopList = [...shops, newShop];
    const sortedShopList = newShopList.sort((a, b) =>
      a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
    );
    setShops(sortedShopList);
    setEditShop(false);
    setOpen(false);
    const data = { newShop: newShop };

    try {
      const addedShop = await axios.post(
        "/api/shops/addshop",
        data,
        requestConfig
      );
      console.log("Added the following shop to the database: ", addedShop);
    } catch (err) {
      console.log("Error adding shop to the backend: ", err);
      alert("There was an error adding the shop");
      setShops(shops.filter((shop) => shop.id !== id));
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleDeleteShop = async (id) => {
    if (window.confirm("Do you want to delete this shop?")) {
      const oldShops = shops;
      setShops(shops.filter((shop) => shop.id !== id));

      const requestData = {
        headers: {
          Authorization: "Bearer " + auth.token,
        },
        params: {
          deletedShopId: id,
        },
      };

      try {
        console.log("Here is the auth token: ", requestData);
        
        const response = await axios.delete("/api/shops/deleteshop", requestData);
        console.log("Deleted the following shop: ", response);

      } catch (err) {
        console.log("Error deleting the shop in the backend: ", err);
        alert(err.response.data.message);
        setShops(oldShops);
      }
    }
  };

  const handleUpdateShop = async (updatedShop) => {
    console.log("Here is the updated shop info: ", updatedShop);
    setOpen(false);
    const previousShops = shops;
    const updatedShops = shops.map((shop) => {
      if (shop.id === updatedShop.id) {
        return { ...updatedShop };
      } else {
        return shop;
      }
    });

    const data = { updatedShop: updatedShop };

    try {
      const modifiedShop = await axios.put(
        "/api/shops/modifyshop",
        data,
        requestConfig
      );
      console.log("Modified the following shop in the database: ", modifiedShop.data.modifiedShop);
    } catch (err) {
      console.log("there was an error updating the shop: ", err);
      alert("We were unable to update the shop");
      setShops(previousShops);
    }

    setShops(updatedShops);
  };

  const handleEditShop = (id) => {
    const shop = id.row;
    setCurrentShop({
      id: shop.id,
      name: shop.name,
      description: shop.description || ""
    });
    setEditShop(true);
    setOpen(true);
    console.log("Edit Shop: ", id.row);
  };

  const columns = [
    {
      field: "name",
      headerName: "Shop Name",
      width: 200,
      editable: false,
    },
    {
      field: "description",
      headerName: "Description",
      width: 400,
      editable: false,
    },
    {
      field: "actions",
      type: "actions",
      width: 100,
      headerName: "",
      cellClassName: "actions",
      getActions: (id) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          name="edit"
          label="Edit"
          onClick={() => {
            handleEditShop(id);
          }}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon color="action" />}
          name="delete"
          label="Delete"
          onClick={() => {
            handleDeleteShop(id.id);
          }}
          color="inherit"
        />,
      ],
    },
  ];

  return (
    <Dialog 
      open={true} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Manage Shops</span>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ 
          width: "100%", 
          height: "100%",
          paddingTop: 2 }}>
          <Button variant="contained" onClick={() => handleOpen()}>
            Add Shop
          </Button>
          <ShopModal
            shop={currentShop}
            editShop={editShop}
            open={open}
            handleCancel={handleCancel}
            handleAddShop={handleAddShop}
            handleUpdateShop={handleUpdateShop}
          />
          <Box sx={{ 
            width: "100%", 
            height: "calc(100% - 60px)",
            paddingTop: 2 }}>
            <DataGrid
              getRowId={(row) => row.id}
              columns={columns}
              rows={shops}
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar:{
                  csvOptions: { disableToolbarButton: true },
                  printOptions: { disableToolbarButton: true },
                  showQuickFilter: true,
                }
              }}
              disableDensitySelector={true}
              disableColumnSelector={true}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const ShopModal = ({
  shop,
  editShop,
  open,
  handleCancel,
  handleAddShop,
  handleUpdateShop,
}) => {
  const [newShop, setNewShop] = useState(shop);

  useEffect(() => {
    if (shop) {
      setNewShop(shop);
    }
  }, [shop]);

  const handleChange = (e) => {
    e.preventDefault();
    const itemChanged = e.target.name;
    const valueChanged = e.target.value;
    const changeShop = { ...newShop, [itemChanged]: valueChanged };
    setNewShop(changeShop);
  };

  const handleClose = () => {
    setNewShop({
      name: "",
      description: ""
    });
    handleCancel();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    editShop ? handleUpdateShop(newShop) : handleAddShop(newShop);

    setNewShop({
      name: "",
      description: ""
    });
  };

  return (
    <Dialog open={open}>
      <DialogTitle>{editShop ? "Edit Shop" : "New Shop"}</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <TextField
            required
            autoFocus
            fullWidth
            margin="normal"
            id="name"
            label="Shop Name"
            name="name"
            autoComplete="off"
            type="text"
            value={newShop.name}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="normal"
            id="description"
            label="Description"
            name="description"
            autoComplete="off"
            type="text"
            value={newShop.description}
            onChange={handleChange}
            multiline
            rows={3}
          />
          <DialogActions>
            <Button type="submit" variant="contained" sx={{ width: "40%" }}>
              {editShop ? "Update Shop" : "Add Shop"}
            </Button>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ width: "40%" }}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
}; 